<script setup lang="ts">
import type { Content, Page } from '@bloomreach/spa-sdk'
import {
    EBannerRowSize,
    EMobileOrientation,
    ETitleSizes,
    type IBannerContent,
    type TBannerLayout,
    type TBannerRowSize,
    type TMobileOrientation,
    type TTitleSizes,
} from '../ts/types'
import type { IRef } from '../../../ts/interfaces/IRef'
import Banner from './Banner.vue'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import { getContent } from '@/cms-ui/utils/getContent'

const props = defineProps<{
    size?: TBannerRowSize
    banners?: IRef[]
    layout?: TBannerLayout
    mobileOrientation?: TMobileOrientation
    displayName?: string
    page: Page
    darkmode?: boolean
}>()
const bannersWithContent: IBannerContent[] = []
props.banners?.forEach(item =>
    bannersWithContent.push(getContent<Content>(item, props.page)?.getData() as unknown as IBannerContent),
)
const layout = props.layout?.split('-')

const { isMobile, isTablet } = useBreakpoint()

const tabletMapping = {
    '12': '8',
    '9-3': '5-3',
    '8-4': '5-3',
    '3-9': '3-5',
    '4-8': '3-5',
    '6-6': '4-4',
    '4-4-4': '4-4-8',
    '3-3-6': '4-4-8',
    '6-3-3': '8-4-4',
    '3-3-3-3': '4-4-4-4',
}

function getColSpan(index: number, tablet: boolean): string {
    const tabletLayout = tabletMapping[props.layout || '12'].split('-')
    const width = tablet ? tabletLayout[index] : layout?.[index]
    return width || '8'
}

const desktopTitleSizeMap = {
    [EBannerRowSize.Small]: {
        '12': ETitleSizes.DISPLAY_SM,
        '9': ETitleSizes.DISPLAY_SM,
        '8': ETitleSizes.DISPLAY_XS,
        '6': ETitleSizes.DISPLAY_XS,
        '4': ETitleSizes.DISPLAY_XS_SMALLER,
        '3': ETitleSizes.DISPLAY_XS_SMALLER,
    },
    [EBannerRowSize.Medium]: {
        '12': ETitleSizes.DISPLAY_MD,
        '9': ETitleSizes.DISPLAY_MD,
        '8': ETitleSizes.DISPLAY_SM,
        '6': ETitleSizes.DISPLAY_SM,
        '4': ETitleSizes.DISPLAY_XS,
        '3': ETitleSizes.DISPLAY_XS,
    },
    [EBannerRowSize.Large]: {
        '12': ETitleSizes.DISPLAY_MD,
        '9': ETitleSizes.DISPLAY_MD,
        '8': ETitleSizes.DISPLAY_SM,
        '6': ETitleSizes.DISPLAY_SM,
        '4': ETitleSizes.DISPLAY_XS,
        '3': ETitleSizes.DISPLAY_XS,
    },
}

const tabletTitleSizeMap = {
    [EBannerRowSize.Small]: {
        '8': ETitleSizes.DISPLAY_XS,
        '5': ETitleSizes.DISPLAY_XS_SMALLER,
        '4': ETitleSizes.DISPLAY_XS_SMALLER,
        '3': ETitleSizes.DISPLAY_2XS,
    },
    [EBannerRowSize.Medium]: {
        '8': ETitleSizes.DISPLAY_SM,
        '5': ETitleSizes.DISPLAY_XS,
        '4': ETitleSizes.DISPLAY_XS,
        '3': ETitleSizes.DISPLAY_XS_SMALLER,
    },
    [EBannerRowSize.Large]: {
        '8': ETitleSizes.DISPLAY_SM,
        '5': ETitleSizes.DISPLAY_XS,
        '4': ETitleSizes.DISPLAY_XS,
        '3': ETitleSizes.DISPLAY_XS_SMALLER,
    },
}

function getTitleSize(index: number): TTitleSizes {
    const colspan = getColSpan(index, isTablet.value)
    const sizeMap = isTablet.value ? tabletTitleSizeMap : desktopTitleSizeMap

    return (sizeMap[props.size || EBannerRowSize.Medium] as Record<string, ETitleSizes>)?.[colspan]
}
</script>

<template>
    <div class="container-grid gap-4 lg:gap-6">
        <div
            v-if="isMobile && mobileOrientation === EMobileOrientation.SWIPE"
            class="col-span-full"
        >
            <VCarousel
                :carousel-aria-label="`carousel_${displayName}`"
                :data-test="`carousel_${displayName}`"
                :dark="darkmode"
            >
                <template
                    v-for="(banner, index) in bannersWithContent"
                    :key="`${banner?.name}_${index}`"
                >
                    <Banner
                        :dark="banner?.darkMode"
                        :data-test="`banner_${banner?.id}`"
                        :title="banner?.heading"
                        :title-size="getTitleSize(index)"
                        :paragraph="banner?.paragraph?.value"
                        :size="size"
                        :image="banner?.image"
                        :image-alt="banner?.image?.altText"
                        :seo-heading-size="banner?.seoHeadingSize?.value"
                        class="mr-2"
                        :class="{ 'ml-2': index === 0, 'mr-4': index === bannersWithContent.length - 1 }"
                        :badge="banner?.badge"
                        :button="banner?.button"
                        :background="banner?.background"
                        :page="page"
                        :image-placement-mobile="banner?.mobileImagePosition"
                        :image-placement-desktop="banner?.desktopImagePosition"
                        carousel
                    />
                </template>

                <template #bottom>
                    <VCarouselScrollbar />
                </template>
            </VCarousel>
        </div>

        <template
            v-for="(banner, index) in bannersWithContent"
            v-else
            :key="`${banner?.name}_${index}`"
        >
            <Banner
                :dark="banner?.darkMode"
                :data-test="`banner_${banner?.id}`"
                :title="banner?.heading"
                :title-size="getTitleSize(index)"
                :seo-heading-size="banner?.seoHeadingSize?.value"
                :paragraph="banner?.paragraph?.value"
                :size="isMobile ? banner?.mobileHeight : size"
                :image="banner?.image"
                :image-alt="banner?.image?.altText"
                class="max-md:col-span-full"
                :class="`max-lg:col-span-${getColSpan(index, true)} lg:col-span-${getColSpan(index, false)}`"
                :badge="banner?.badge"
                :button="banner?.button"
                :background="banner?.background"
                :page="page"
                :image-placement-mobile="banner?.mobileImagePosition"
                :image-placement-desktop="banner?.desktopImagePosition"
            />
        </template>
    </div>
</template>
